import { Box, Stack, Typography } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1MultiUserDisplay, C1MultiUserDisplayWithFetch } from 'components/core/multi-user/C1MultiUserDisplay'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { TaskValue } from 'lib/actions/task'
import { UserX } from 'lucide-react'
import { Provision, ProvisionInstance, ProvisionInstanceState } from 'pbts/c1/api/policy/v1/policy'
import { Approval, ApprovalInstance, ApprovalInstanceState } from 'pbts/c1/api/policy/v1/policy_steps_approval'
import { TaskProcessingType } from 'pbts/c1/api/task/v1/task'
import { User } from 'pbts/c1/api/user/v1/user'
import { FC, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { ActionableUserListContainerStyles } from './styles'

type ActionableUserListContainerType = {
    children: ReactNode
}

const ActionableUserListContainer: FC<ActionableUserListContainerType> = ({ children }) => {
    return (
        <Box sx={ActionableUserListContainerStyles.container}>
            <Box sx={ActionableUserListContainerStyles.userListContainer}>{children}</Box>
        </Box>
    )
}

export const DisplayActionableUserList = ({
    currentStep,
    approval,
    provision,
    task,
}: {
    currentStep: boolean
    approval?: ApprovalInstance
    provision?: ProvisionInstance
    task: TaskValue
}): React.ReactElement => {
    if (!approval && !provision) {
        return <></>
    }

    if (approval?.state === ApprovalInstanceState.DONE) {
        switch (approval?.outcome.oneofKind) {
            case 'approved':
                return <ActionableUserListContainer>
                    <C1MultiUserDisplayWithFetch userIds={[approval.outcome.approved.userId]} />
                </ActionableUserListContainer>

            case 'skipped':
                return <ActionableUserListContainer>
                    <C1MultiUserDisplayWithFetch userIds={[approval.outcome.skipped.userId]} />
                </ActionableUserListContainer>

            case 'denied':
                return <ActionableUserListContainer>
                    <C1MultiUserDisplayWithFetch userIds={[approval.outcome.denied.userId]} />
                </ActionableUserListContainer>
            default:
                return <></>
        }
    }

    if (provision?.state === ProvisionInstanceState.DONE) {
        switch (provision?.outcome.oneofKind) {
            case 'completed':
                return <ActionableUserListContainer>
                    <C1MultiUserDisplayWithFetch userIds={[provision.outcome.completed.userId]} />
                </ActionableUserListContainer>

            case 'skipped':
                return <ActionableUserListContainer>
                    <C1MultiUserDisplayWithFetch userIds={[provision.outcome.skipped.userId]} />
                </ActionableUserListContainer>

            case 'cancelled':
                return <ActionableUserListContainer>
                    <C1MultiUserDisplayWithFetch userIds={[provision.outcome.cancelled.cancelledByUserId]} />
                </ActionableUserListContainer>

            default:
                return <></>
        }
    }

    return (
        <ActionableUsersList
            currentStep={currentStep}
            stepApprovers={task.stepApprovers}
            provision={provision?.provision}
            approval={approval?.approval}
            isTaskProcessing={task.processing === TaskProcessingType.PROCESSING}
        />
    )
}

export const NoApproverDisplay = ({ isTaskProcessing }: { isTaskProcessing: boolean }): React.ReactElement => {
    if (isTaskProcessing) {
        return <C1EmptyIndicator />
    }
    return (
        <Stack direction="row" spacing={1}>
            <C1Icon icon={UserX} size="sm" sx={{ color: 'error.main' }} />
            <Typography color="error">
                <FormattedMessage
                    id="components_task_review_details_no_approver_assigned"
                    defaultMessage="No assigned user"
                />
            </Typography>
        </Stack>
    )
}

const ActionableUsersList = ({
    stepApprovers,
    approval,
    provision,
    currentStep,
    isTaskProcessing,
}: {
    stepApprovers?: User[]
    approval?: Approval
    provision?: Provision
    currentStep: boolean
    isTaskProcessing: boolean
}): React.ReactElement => {
    if (currentStep && !approval?.assigned && !provision?.assigned) {
        return (
            <ActionableUserListContainer>
                <Box display="flex" flexDirection="row">
                    <Box sx={{ flexGrow: 1 }} mr={1}>
                        <NoApproverDisplay isTaskProcessing={isTaskProcessing} />
                    </Box>
                </Box>
            </ActionableUserListContainer>
        )
    }

    if (approval !== undefined && approval.typ.oneofKind === 'group') {
        return (
            <ActionableUserListContainer>
                <C1EntitlementDisplayNameWithFetch
                    entitlementId={approval.typ.group.appGroupId}
                    appId={approval.typ.group.appId}
                />
            </ActionableUserListContainer>
        )
    } if (stepApprovers) {
        return (
            <ActionableUserListContainer>
                <C1MultiUserDisplay users={stepApprovers} />
            </ActionableUserListContainer>
        )
    }
    return <></>
}
